import {HStack, Span, Stack, Text} from "@chakra-ui/react";
import type {OwnerPayload, ReviewAction, ReviewerRole} from "platformed-client/types";
import {Tag} from "../../../components/ui/tag";
import {OwnerAvatar} from "../../../components/OwnerAvatar";
import T from "platformed-components/i18n/T";
import {AddReviewersPopover} from "./AddReviewersPopover";
import {ReviewerRow} from "./ReviewerRow";
import type {ApplyOutcome, ApplyPlan, ContentId} from "./reviewCandidates";
import type {ReviewMap} from "./reviewsShared";
import type {ReviewItem} from ".";

export type RoleSectionProps = {
  role: ReviewerRole;
  /** This role's reviewers, in role order. */
  reviews: ReviewAction[];
  /** Every review on the page, so the picker can block people holding another role. */
  allReviews: ReviewMap;
  roles: ReviewerRole[];
  owners: OwnerPayload[];
  reviewItem: ReviewItem;
  descendantPageCount: number;
  /** Changes waiting to be saved, when the surface defers writes. */
  staged?: ApplyPlan;
  /** Roles queued across every section, so this one can block people already claimed. */
  stagedRoles?: Map<ContentId, ReviewerRole>;
  onApply: (plan: ApplyPlan) => Promise<ApplyOutcome>;
};

export const RoleSection = ({
  role,
  reviews,
  allReviews,
  roles,
  owners,
  reviewItem,
  descendantPageCount,
  staged,
  stagedRoles,
  onApply,
}: RoleSectionProps) => {
  const removedIds = new Set(staged?.removed.map(r => r.reviewer.owner_id) ?? []);
  const count = reviews.length - removedIds.size + (staged?.added.length ?? 0);

  return (
    <Stack gap={1}>
      <HStack gap={2} pb={1.5} borderBottomWidth="1px" borderColor="border" align="center">
        <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase" letterSpacing="wide">
          {role.name}
        </Text>
        {count > 0 && (
          <Span fontSize="xs" fontWeight="semibold" color="fg.muted" bg="bg.muted" rounded="full" px={1.5}>
            {count}
          </Span>
        )}
        <Span flex={1} />
        <AddReviewersPopover
          role={role}
          reviews={allReviews}
          roles={roles}
          owners={owners}
          reviewerCount={count}
          descendantPageCount={descendantPageCount}
          staged={staged}
          stagedRoles={stagedRoles}
          onApply={onApply}
        />
      </HStack>

      {reviews.length === 0 && !staged?.added.length ? (
        <Text fontSize="sm" color="fg.subtle" py={1}>
          <T keyName="c.No_role_assigned[parameterized]" params={{role: role.name.toLowerCase()}} />
        </Text>
      ) : (
        reviews.map(review => (
          <ReviewerRow
            key={review.reviewer.owner_id}
            review={review}
            reviewItem={reviewItem}
            pendingRemoval={removedIds.has(review.reviewer.owner_id)}
          />
        ))
      )}

      {/* Additions the user has queued but not yet saved. */}
      {staged?.added.map(candidate => (
        <HStack key={candidate.contentId} gap={2} py={1.5} opacity={0.75}>
          <OwnerAvatar owner={candidate.owner} />
          <Tag size="sm" colorPalette="green">
            <T keyName="c.Will_be_requested" />
          </Tag>
        </HStack>
      ))}
    </Stack>
  );
};
